import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { formatDuration, formatMoment, historyLine } from "@/lib/slots";

const STATUSES = ["pending", "confirmed", "assigned", "in_progress", "completed", "cancelled", "rejected", "rescheduled"];

export default function TAdminBookings() {
  const [list, setList] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [filter, setFilter] = useState("");
  // The booking whose history is open.
  const [open, setOpen] = useState(null);

  const load = async () => {
    const [b, s] = await Promise.all([
      api.get(`/tenant/bookings${filter ? `?status=${filter}` : ""}`),
      api.get("/tenant/sellers"),
    ]);
    setList(b.data); setSellers(s.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (b, status, seller_id) => {
    try {
      await api.put(`/tenant/bookings/${b.id}/status`, { status, seller_id });
      toast.success("Updated");
    } catch (err) {
      // The server refuses a change that would double book a slot or a
      // professional, or that the booking's status does not allow.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The booking could not be changed");
    }
    load();       // either way, show what the booking is now
  };

  // When the job was started and ended, by the professional or the business.
  const Times = ({ b }) => (b.started_at || b.completed_at) ? (
    <div className="text-[11px] text-neutral-600 mt-1" data-testid={`tadmin-booking-times-${b.id}`}>
      {b.started_at && <div>Started {formatMoment(b.started_at)}</div>}
      {b.completed_at && <div>Ended {formatMoment(b.completed_at)}{b.started_at ? ` · ${formatDuration(b.started_at, b.completed_at)}` : ""}</div>}
    </div>
  ) : null;

  // Every change to the booking, oldest first: what, when and by whom.
  const History = ({ b }) => (
    <ol className="text-xs text-neutral-700 space-y-1" data-testid={`tadmin-booking-history-${b.id}`}>
      {(b.history || []).length === 0 && <li className="text-neutral-500">No history was recorded for this booking (it was made before history was kept).</li>}
      {(b.history || []).map((h, i) => (
        <li key={i} className="flex flex-wrap gap-x-2"><span className="font-mono text-neutral-500">{formatMoment(h.at)}</span><span className="break-words">{historyLine(h)}</span></li>
      ))}
    </ol>
  );
  const HistoryToggle = ({ b }) => (
    <button type="button" onClick={() => setOpen(open === b.id ? null : b.id)} aria-expanded={open === b.id}
      className="text-[11px] underline text-neutral-600 hover:text-black min-h-[32px]" data-testid={`tadmin-booking-history-toggle-${b.id}`}>
      {open === b.id ? "Hide history" : "History"}
    </button>
  );

  return (
    <div data-testid="tadmin-bookings">
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div><div className="admin-overline">Operations</div><h1 className="admin-heading text-3xl font-bold mt-1">Bookings</h1></div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by status" className="border border-neutral-300 bg-white px-3 py-2 text-xs min-h-[44px]" data-testid="tadmin-bookings-filter">
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Phones: one card per booking */}
      <div className="md:hidden space-y-3" data-testid="tadmin-bookings-cards">
        {list.length === 0 && <div className="bg-white border border-neutral-200 py-8 text-center text-neutral-500 text-sm">No bookings.</div>}
        {list.map((b) => (
          <div key={b.id} className="bg-white border border-neutral-200 p-4">
            <div className="flex justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[11px] text-neutral-500">{b.code}</div>
                <div className="font-semibold break-words">{b.service_name}</div>
              </div>
              <div className="font-bold flex-shrink-0">{formatINR(b.total)}</div>
            </div>
            <div className="mt-2 text-xs text-neutral-600 break-words">{b.customer_name} · {b.customer_phone}</div>
            <div className="text-xs text-neutral-600">{b.scheduled_date} · {b.scheduled_slot}</div>
            <Times b={b} />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <select value={b.seller_id || ""} onChange={(e) => updateStatus(b, b.status, e.target.value)} aria-label="Seller" className="border border-neutral-300 bg-white px-2 text-xs min-h-[44px] min-w-0">
                <option value="">— Unassigned —</option>
                {sellers.map((s) => <option key={s.id} value={s.id}>{s.business_name}</option>)}
              </select>
              <select value={b.status} onChange={(e) => updateStatus(b, e.target.value)} aria-label="Status" className="border border-neutral-300 bg-white px-2 text-xs uppercase min-h-[44px] min-w-0">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-2"><HistoryToggle b={b} /></div>
            {open === b.id && <div className="mt-1 border-t border-neutral-100 pt-2"><History b={b} /></div>}
          </div>
        ))}
      </div>

      <div className="hidden md:block bg-white border border-neutral-200 table-scroll" data-testid="tadmin-bookings-table">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50"><tr className="text-left">
            <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Code</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Service</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Customer</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">When</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Seller</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Status</th>
            <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Total</th>
          </tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan="7" className="py-8 text-center text-neutral-500 text-sm">No bookings.</td></tr>}
            {list.map((b) => (
              <tr key={b.id} className="border-b border-neutral-100 hover:bg-neutral-50 align-top">
                <td className="px-3 py-3 font-mono text-xs">{b.code}<br /><HistoryToggle b={b} />
                  {open === b.id && <div className="mt-1 font-sans min-w-[260px]"><History b={b} /></div>}</td>
                <td className="px-3 py-3">{b.service_name}</td>
                <td className="px-3 py-3 text-xs">{b.customer_name}<br /><span className="text-neutral-500">{b.customer_phone}</span></td>
                <td className="px-3 py-3 text-xs">{b.scheduled_date}<br /><span className="text-neutral-500">{b.scheduled_slot}</span><Times b={b} /></td>
                <td className="px-3 py-3">
                  <select value={b.seller_id || ""} onChange={(e) => updateStatus(b, b.status, e.target.value)} className="border border-neutral-300 bg-white px-2 py-1 text-xs" data-testid={`tadmin-booking-seller-${b.id}`}>
                    <option value="">— Unassigned —</option>
                    {sellers.map((s) => <option key={s.id} value={s.id}>{s.business_name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3">
                  <select value={b.status} onChange={(e) => updateStatus(b, e.target.value)} className="border border-neutral-300 bg-white px-2 py-1 text-xs uppercase" data-testid={`tadmin-booking-status-${b.id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3 font-bold">{formatINR(b.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
